import { type FC } from 'react';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import LazyImage from 'dibs-react-lazy-image';
import { Link } from 'dibs-elements/exports/Link';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { trackArticleClick } from './tracking';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

import styles from './styles/ArticleLink.scss';
import dibsCss from 'dibs-css';

const getHeaderId = (index: number): string => `wp-article-header-${index}`;
const getDescriptionId = (index: number): string => `wp-article-description-${index}`;
const LAZY_IMAGE_VERTICAL_OFFSET = 500;

import { type ArticleLink_article$data } from './__generated__/ArticleLink_article.graphql';

type Props = {
    article: ArticleLink_article$data;
    index: number;
    stacked?: boolean;
    hidden?: boolean;
    onClick: () => void;
};

const ArticleLink: FC<Props> = ({ article, index, stacked, hidden, onClick }) => {
    const { title, imageUrl, subtitle, link, source } = article;
    const descriptionId = getDescriptionId(index);

    const articleElementClasses = classnames(
        styles.articleElement,
        dibsCss.flex1,
        dibsCss.basis0,
        dibsCss.relative
    );

    return (
        <HeadingLevel>
            {Heading => (
                <Link
                    ariaLabel={`Read more about ${title}`}
                    ariaDescribedBy={`${getHeaderId(index)},${descriptionId}`}
                    buyerLinkType="standardLinkNoEffect"
                    underline="none"
                    href={link}
                    target="_blank"
                    dataTn={`wp-article-link-${index}`}
                    className={classnames(
                        styles.article,
                        dibsCss.flex,
                        dibsCss.justifyBetween,
                        dibsCss.wFull,
                        {
                            [styles.stacked]: stacked,
                            [styles.hidden]: hidden,
                        }
                    )}
                    onClick={() => {
                        trackArticleClick({ title, source });
                        onClick();
                    }}
                >
                    <div className={articleElementClasses}>
                        <LazyImage
                            offsetVertical={LAZY_IMAGE_VERTICAL_OFFSET}
                            placeholderClass={classnames(dibsCss.minWFull, dibsCss.minHFull)}
                        >
                            <img
                                className={classnames(
                                    dibsCss.absolute,
                                    dibsCss.hFull,
                                    dibsCss.wFull,
                                    dibsCss.objectCover
                                )}
                                src={imageUrl.full}
                                srcSet={getSrcsetString(imageUrl.full)}
                                sizes="(max-width: 320px) 100vw, 25vw"
                                alt={title}
                            />
                        </LazyImage>
                    </div>
                    <div className={articleElementClasses}>
                        <Heading
                            className={classnames(
                                styles.header,
                                styles.wordBreak,
                                dibsCss.sassyFontHeaderMedium,
                                dibsCss.mt0,
                                dibsCss.mbXsmall,
                                dibsCss.overflowEllipsis
                            )}
                            id={getHeaderId(index)}
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                        <p
                            className={classnames(
                                styles.body,
                                styles.wordBreak,
                                dibsCss.sassyFontBody,
                                dibsCss.m0,
                                dibsCss.overflowEllipsis
                            )}
                            id={descriptionId}
                            dangerouslySetInnerHTML={{
                                __html: subtitle,
                            }}
                        />
                    </div>
                </Link>
            )}
        </HeadingLevel>
    );
};

export default createFragmentContainer(ArticleLink, {
    article: graphql`
        fragment ArticleLink_article on WordpressArticleType {
            title
            imageUrl {
                full
            }
            subtitle
            link
            source
        }
    `,
});
